import { createContext, use, useSyncExternalStore } from 'react'
import type {
  Attachment,
  BalanceRead,
  ChatPlanRead,
  ConnectRequestRead,
  MessageRead,
  PendingTurnRead,
  PermissionAction,
  PermissionDecision,
  PermissionRead,
  PlanDecision,
  PlanStepInput,
  SummarizationScope,
  ThreadRead,
} from '../../client'
import type { Block } from '../../lib/inFlight'

export type ChatSettings = { useBrandBook: boolean; useFiles: boolean }

export const NEW_CHAT_SETTINGS: ChatSettings = { useBrandBook: true, useFiles: true }

export type Chat = {
  brandId?: number
  threads: ThreadRead[]
  tabs: ThreadRead[]
  activeId: string | null
  messages: MessageRead[]
  permissions: PermissionRead[]
  balance: BalanceRead | null
  pendingPermission: PendingTurnRead | null
  pendingSummarization: SummarizationScope[] | null
  connectRequest: ConnectRequestRead | null
  plans: ChatPlanRead[]
  planMode: boolean
  error: string | null
  busy: 'sending' | 'deciding' | 'summarizing' | null
  loadingMessages: boolean
  loadingMoreThreads: boolean
  hasMoreThreads: boolean
  hasMoreMessages: boolean
  locked: boolean
  openThread: (threadId: string) => Promise<void>
  newThread: () => void
  closeTab: (threadId: string) => void
  send: (userMessage: string, attachments: Attachment[], settings?: ChatSettings) => Promise<void>
  decide: (decision: PermissionDecision) => Promise<void>
  setPlanMode: (on: boolean) => void
  decidePlan: (planId: string, decision: PlanDecision, feedback?: string) => Promise<void>
  editPlan: (planId: string, steps: PlanStepInput[]) => Promise<void>
  summarize: (scope: SummarizationScope) => Promise<void>
  rename: (threadId: string, title: string) => Promise<void>
  archive: (threadId: string) => Promise<void>
  loadMoreThreads: () => Promise<void>
  loadOlderMessages: () => Promise<void>
  setFeedback: (messageId: string, feedback: string | null) => Promise<void>
  revoke: (toolName: string, action: PermissionAction) => Promise<void>
  refreshThreads: () => Promise<ThreadRead[]>
}

export const ChatContext = createContext<Chat | null>(null)

/** The streaming turn is kept out of React state: it changes ~60 times a second, and going
 * through the provider would re-render every chat consumer on each tick. */
export type InFlightStore = {
  subscribe: (listener: () => void) => () => void
  get: () => Block[] | null
}

const EMPTY_STORE: InFlightStore = { subscribe: () => () => undefined, get: () => null }

export const InFlightContext = createContext<InFlightStore>(EMPTY_STORE)

export function useChat() {
  const chat = use(ChatContext)
  if (!chat) throw new Error('useChat must be used inside <ChatProvider>')
  return chat
}

export function useInFlight() {
  const store = use(InFlightContext)
  return useSyncExternalStore(store.subscribe, store.get)
}
